// supervisor-outcome-router-quota-path.ts
//
// Nhánh quota/rate_limit của supervisor-outcome-router.ts, tách ra theo đúng
// khuôn `*-done-path.ts` để router giữ ngân sách 200 dòng. Hành vi không đổi.

import { enterQuotaSleep, type QuotaConfig } from "./quota-sleep-scheduler.js";
import { type RetryConfig, type RetryState, recordAttemptOutcome } from "./retry-policy.js";
import type { AttemptCycleResult } from "./supervisor-attempt-cycle.js";

export interface QuotaPathInput {
  cycle: AttemptCycleResult;
  retryState: RetryState;
  retryConfig: RetryConfig;
  quotaConfig: QuotaConfig;
  quotaStatePath: string;
  backoffIndex: number;
  now: Date;
  beforeSleep?: () => Promise<void>;
}

export type QuotaPathResult = { kind: "quota_sleep"; wakeAt: string };

/** Attempt dừng vì hết quota/rate limit — KHÔNG phải một lần thất bại của worker. */
export function isQuotaOutcome(cycle: AttemptCycleResult): boolean {
  if (cycle.kind === "contract_error") return false;
  const category = cycle.sessionResult.category;
  return category === "quota" || category === "rate_limit";
}

/** Ngủ tới khi quota mở lại, ghi nhận attempt, trả mốc thức dậy cho caller. */
export async function routeQuotaOutcome(input: QuotaPathInput): Promise<QuotaPathResult> {
  const reason =
    input.cycle.kind === "verified"
      ? (input.cycle.sessionResult.reason ?? "quota")
      : input.cycle.reason;
  const state = await enterQuotaSleep({
    statePath: input.quotaStatePath,
    now: input.now,
    reason,
    config: input.quotaConfig,
    backoffIndex: input.backoffIndex,
    // The provider's own reset-time text (§6.4: "parse reset có
    // timezone... hoặc backoff ladder") — computeWakeAt tries this FIRST,
    // falling back to the injected backoff_min ladder only when it can't
    // find a parseable "resets at HH:MM" in it.
    resetTimeText: reason,
    beforeSleep: input.beforeSleep,
  });
  // Quota still counts as a physical run (§6.4/F04) even though it never
  // reaches counted_failures — recorded via retry-policy's own outcome
  // path so both counters stay derived from ONE call site.
  recordAttemptOutcome(
    input.retryState,
    { category: "quota", evidence: state.reason },
    input.retryConfig,
    input.now,
  );
  return { kind: "quota_sleep", wakeAt: state.wake_at };
}
